import React, { useEffect, useState } from 'react'
import { Card, Row, Col, Statistic, Button, Rate, Tag, Select, Modal, message, Pagination } from 'antd'
import { BookOutlined, ImportOutlined, SearchOutlined, AppstoreAddOutlined, ShareAltOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { computeApiBaseURL, request } from '../utils/request'
import { useAuthStore } from '../stores/auth'
import { buildSiteSharePayload, shareContent } from '@/utils/share'

interface Book {
  id: string
  title: string
  author?: string
  format: string
  size: string
  uploadTime: string
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

type PublicRecItem = {
  id: number
  name: string
  author: string
  format: string
  source: 'strong' | 'rating'
  avg_rating?: number
  rating_count?: number
}

const getBookCoverUrl = (bookId: number) => {
  try {
    return new URL(`/api/books/${bookId}/cover`, computeApiBaseURL()).toString()
  } catch {
    return `/api/books/${bookId}/cover`
  }
}

const Home: React.FC = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState<{ books: number; today: number }>({ books: 0, today: 0 })
  const [recentBooks, setRecentBooks] = useState<Book[]>([])
  const [sortField, setSortField] = useState<'title' | 'author' | 'format' | 'size' | 'uploadTime'>('uploadTime')
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend')
  const getFieldValue = (o: Book, k: 'title' | 'author' | 'format' | 'size' | 'uploadTime'): string => {
    if (k === 'title') return String(o.title || '')
    if (k === 'author') return String(o.author || '')
    if (k === 'format') return String(o.format || '')
    if (k === 'size') return String(o.size || '')
    return String(o.uploadTime || '')
  }
  const sortedRecentBooks = React.useMemo(() => {
    const arr = [...recentBooks]
    const cmp = (a: Book, b: Book) => {
      const dir = sortOrder === 'ascend' ? 1 : -1
      if (sortField === 'size') {
        const pa = parseFloat(String(a.size || '').replace(/[^0-9.]/g, '')) || 0
        const pb = parseFloat(String(b.size || '').replace(/[^0-9.]/g, '')) || 0
        return dir * (pa - pb)
      }
      if (sortField === 'uploadTime') {
        return dir * (new Date(a.uploadTime).getTime() - new Date(b.uploadTime).getTime())
      }
      const va = getFieldValue(a, sortField)
      const vb = getFieldValue(b, sortField)
      return dir * va.localeCompare(vb)
    }
    arr.sort(cmp)
    return arr
  }, [recentBooks, sortField, sortOrder])
  const [publicRecs, setPublicRecs] = useState<PublicRecItem[]>([])
  const [publicRecsPage, setPublicRecsPage] = useState<number>(1)
  const [publicRecsPageSize, setPublicRecsPageSize] = useState<number>(24)
  const [hotCount, setHotCount] = useState<number>(0)

  const { user, isAuthenticated } = useAuthStore()
  const [canUpload, setCanUpload] = useState<boolean>(false)
  const [recentImportVisible, setRecentImportVisible] = useState<boolean>(false)
  const [siteName, setSiteName] = useState('伴读么 - Ai角色朗读电子书屋')
  const [marqueeContent, setMarqueeContent] = useState('')
  const marqueeContainerRef = React.useRef<HTMLDivElement | null>(null)
  const marqueeMeasureRef = React.useRef<HTMLSpanElement | null>(null)
  const [marqueeRepeat, setMarqueeRepeat] = React.useState(2)
  const [marqueeShiftPx, setMarqueeShiftPx] = React.useState(0)
  const [marqueeDurationSec, setMarqueeDurationSec] = React.useState(30)
  const [marqueeGapPx, setMarqueeGapPx] = React.useState(64)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [sharingSite, setSharingSite] = useState(false)
  const [resumePath, setResumePath] = useState('')
  const [resumeAt, setResumeAt] = useState(0)
  const role = user?.role || 'guest'
  const canViewRecentImports = isAuthenticated && (
    role === 'admin' ||
    role === 'super_admin' ||
    recentImportVisible
  )

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    try {
      const skipKey = 'ebookhub:skip-home-autoresume-once'
      const consumedKey = 'ebookhub:home-autoresume-consumed'
      const skipOnce = sessionStorage.getItem(skipKey) === '1'
      if (skipOnce) {
        try { sessionStorage.removeItem(skipKey) } catch { /* noop */ }
        return
      }
      const consumed = sessionStorage.getItem(consumedKey) === '1'
      if (consumed) return
      const raw = localStorage.getItem('ebookhub:last-reader-session')
      if (!raw) return
      const j = JSON.parse(raw) as { path?: unknown; at?: unknown }
      const path = String(j?.path || '').trim()
      const at = Number(j?.at || 0)
      if (!/^\/reader\/.+/.test(path)) return
      if (Number.isFinite(at) && at > 0 && (Date.now() - at) > 30 * 24 * 3600 * 1000) return
      setResumePath(path)
      setResumeAt(Number.isFinite(at) ? at : 0)
    } catch { /* noop */ }
  }, [])

  const handleContinueReading = () => {
    if (!resumePath) return
    try { sessionStorage.setItem('ebookhub:home-autoresume-consumed', '1') } catch { /* noop */ }
    const path = String(resumePath || '').trim()
    if (!/^\/reader\/.+/.test(path)) return
    navigate(path)
  }

  const handleDismissContinueReading = () => {
    setResumePath('')
    setResumeAt(0)
    try { sessionStorage.setItem('ebookhub:home-autoresume-consumed', '1') } catch { /* noop */ }
  }

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt()
      const { outcome } = await installPrompt.userChoice
      if (outcome === 'accepted') {
        setInstallPrompt(null)
      }
    } else {
      const ua = navigator.userAgent.toLowerCase()
      if (/iphone|ipad|ipod/.test(ua)) {
         Modal.info({
           title: '添加到主屏幕',
           content: (
             <div>
               <p>在 Safari 浏览器中：</p>
               <p>1. 点击底部工具栏的分享图标 <span style={{ fontSize: 20 }}>⎋</span></p>
               <p>2. 向下滚动并选择“添加到主屏幕”</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else if (/android|harmony/i.test(ua)) {
         Modal.info({
           title: '添加到主屏幕',
           content: (
             <div>
               <p>请点击浏览器菜单图标 <span style={{ fontSize: 20 }}>⋮</span></p>
               <p>选择“安装应用”、“添加到主屏幕”或“添加至桌面”</p>
               <p className="text-gray-500 text-xs mt-2">适用于安卓、鸿蒙系统及各类国产浏览器</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else if (/windows|macintosh|linux/i.test(ua)) {
         Modal.info({
           title: '安装到桌面',
           content: (
             <div>
               <p>请点击浏览器地址栏右侧的 <span style={{ fontSize: 20 }}>⊕</span> 或 <span style={{ fontSize: 20 }}>⬇</span> 安装图标</p>
               <p>或在菜单中选择“安装 {siteName}”</p>
             </div>
           ),
           maskClosable: true,
           okText: '知道了'
         })
      } else {
         message.info('请使用浏览器的“安装应用”或“添加到主屏幕”功能')
      }
    }
  }

  useEffect(() => {
    const refresh = async () => {
      try {
        const res = await request.get('/api/auth/public-config', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (res.data?.success) {
          setSiteName(res.data.data.site_name || '伴读么 - Ai角色朗读电子书屋')
          setMarqueeContent(res.data.data.marquee_content || '')
          setRecentImportVisible(res.data.data.recent_import_visible === true)
        }
      } catch {
        setRecentImportVisible(false)
      }

      if (!isAuthenticated) {
        setCanUpload(false)
        return
      }

      try {
        const res = await request.get('/api/upload/settings', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const data = res.data?.data || {}
        setCanUpload(Boolean(data.canUpload))
      } catch { setCanUpload(false) }
    }
    const onCfg = () => { refresh() }
    const onPerm = () => { refresh() }
    refresh()
    window.addEventListener('ebookhub:config-changed', onCfg as unknown as EventListener)
    window.addEventListener('ebookhub:permission-changed', onPerm as unknown as EventListener)
    return () => {
      window.removeEventListener('ebookhub:config-changed', onCfg as unknown as EventListener)
      window.removeEventListener('ebookhub:permission-changed', onPerm as unknown as EventListener)
    }
  }, [isAuthenticated, user?.id])
  
  const fetchRecentBooks = async () => {
    try {
      const res = await request.get('/api/admin/recent-books', {
        params: { page: 1, pageSize: 50 },
        headers: { 'X-Skip-401-Redirect': '1' }
      })
      
      if (res.data?.success && res.data?.data) {
        setRecentBooks(Array.isArray(res.data.data) ? res.data.data : [])
      } else {
        setRecentBooks([])
      }
    } catch (error) {
      setRecentBooks([])
    }
  }
  
  useEffect(() => {
    (async () => {
      try {
        // 优先尝试公共统计接口，它对所有用户开放且包含今日新增数据
        const res = await request.get('/api/books/stats/public', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        if (res.data?.success && res.data?.data) {
          setStats({ 
            books: Number(res.data.data.totalBooks || 0), 
            today: Number(res.data.data.todayBooks || 0) 
          })
          return
        }

        // 如果公共接口失败，降级到旧逻辑（仅管理员尝试admin接口）
        if (isAuthenticated && (role === 'admin' || role === 'super_admin')) {
          try {
            const res = await request.get('/api/admin/stats', { headers: { 'X-Skip-401-Redirect': '1' } })
            const data = res.data?.data || {}
            setStats({ books: Number(data?.totalBooks || 0), today: Number((data?.todayUploads ?? data?.todayBooks) || 0) })
          } catch (adminError) {
            const res = await request.get('/api/search?query=&page=1&limit=1', { headers: { 'X-Silent': '1', 'X-Skip-401-Redirect': '1' } })
            const data = res.data?.data || {}
            setStats({ books: Number(data?.pagination?.total || 0), today: 0 })
          }
        } else {
          const res = await request.get('/api/search?query=&page=1&limit=1', { headers: { 'X-Silent': '1' } })
          const data = res.data?.data || {}
          setStats({ books: Number(data?.pagination?.total || 0), today: 0 })
        }
      } catch {
        setStats({ books: 0, today: 0 })
      }
    })()
    
    if (canViewRecentImports) {
      fetchRecentBooks()
    } else {
      setRecentBooks([])
    }
    (async () => {
      try {
        const res = await request.get('/api/books/recommendations/public?limit=200', { headers: { 'X-Skip-401-Redirect': '1', 'X-Silent': '1' } })
        const items = res.data?.data?.items || []
        setPublicRecs(items)
        setPublicRecsPage(1)
        setHotCount(Array.isArray(items) ? items.length : 0)
      } catch { setPublicRecs([]); setHotCount(0) }
    })()
  }, [canViewRecentImports, isAuthenticated, role])

  useEffect(() => {
    if (!marqueeContent) return
    if (typeof window === 'undefined') return
    let raf = 0
    let ro: ResizeObserver | null = null
    const calc = () => {
      const container = marqueeContainerRef.current
      const measure = marqueeMeasureRef.current
      if (!container || !measure) return
      const containerW = Math.max(1, Math.floor(container.clientWidth || 1))
      const contentW = Math.max(1, Math.ceil(measure.scrollWidth || measure.getBoundingClientRect().width || 1))
      const gap = contentW < containerW ? 24 : 64
      const one = contentW + gap
      const need = Math.ceil(containerW / one) + 2
      const repeat = Math.max(2, Math.min(10, need))
      const speed = contentW < containerW ? 36 : 54
      const duration = Math.max(12, Math.min(45, Math.round(one / speed)))
      setMarqueeGapPx(gap)
      setMarqueeShiftPx(one)
      setMarqueeRepeat(repeat)
      setMarqueeDurationSec(duration)
    }
    raf = window.requestAnimationFrame(calc)
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        if (raf) window.cancelAnimationFrame(raf)
        raf = window.requestAnimationFrame(calc)
      })
      if (marqueeContainerRef.current) ro.observe(marqueeContainerRef.current)
    }
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      if (ro) ro.disconnect()
    }
  }, [marqueeContent])

  const pagedPublicRecs = React.useMemo(() => {
    const list = Array.isArray(publicRecs) ? publicRecs : []
    const ps = Math.max(1, Number(publicRecsPageSize || 24))
    const p = Math.max(1, Number(publicRecsPage || 1))
    const start = (p - 1) * ps
    return list.slice(start, start + ps)
  }, [publicRecs, publicRecsPage, publicRecsPageSize])

  const handleShareSite = async () => {
    if (sharingSite) return
    try {
      setSharingSite(true)
      const payload = buildSiteSharePayload({
        siteName,
        tagline: '全格式电子书管理与阅读平台'
      })
      await shareContent(payload)
    } catch {
      message.error('分享失败')
    } finally {
      setSharingSite(false)
    }
  }

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(var(--marquee-shift, 0px) * -1)); }
        }
        .marquee-container {
          white-space: nowrap;
        }
        .animate-marquee {
          display: flex;
          width: fit-content;
          animation: marquee var(--marquee-duration, 30s) linear infinite;
          will-change: transform;
        }
        .animate-marquee > div {
          flex: 0 0 auto;
          white-space: nowrap;
          padding-right: var(--marquee-gap, 64px);
        }
        .marquee-container:hover .animate-marquee {
          animation-play-state: paused;
        }
      `}</style>
      <div className="rounded-2xl bg-gradient-to-br from-orange-100 via-amber-50 to-white p-4 md:p-6 shadow-warm border border-orange-100 relative overflow-hidden group">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-orange-200/40 via-transparent to-transparent opacity-60" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-300/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        
        <div className="relative z-10 flex flex-col gap-6">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 tracking-tight flex items-center gap-3 min-w-0">
                <span className="text-amber-700 break-words">
                  {siteName}
                </span>
              </h1>
              <div className="shrink-0 flex items-center gap-1">
                <Button
                  size="small"
                  type="text"
                  className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-600 hover:text-primary-600 hover:bg-white/70"
                  icon={<AppstoreAddOutlined />}
                  onClick={handleInstall}
                  aria-label="桌面快捷"
                  title="桌面快捷"
                />
                <Button
                  size="small"
                  type="text"
                  className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-600 hover:text-primary-600 hover:bg-white/70"
                  icon={<ShareAltOutlined />}
                  onClick={handleShareSite}
                  loading={sharingSite}
                  aria-label="分享网站"
                  title="分享网站"
                />
              </div>
            </div>
            <p className="mt-3 text-sm md:text-lg text-gray-600 font-medium max-w-xl">
              全格式支持 · 去重与检索 · 多终端阅读同步
            </p>
          </div>
          <div className="flex gap-3 flex-col sm:flex-row flex-wrap w-full">
            {canUpload && (
              <Button type="primary" size="large" className="btn-gradient h-11 px-8 rounded-xl w-full sm:w-auto hover:scale-105 transition-transform" onClick={() => navigate('/import')}>
                立即导入
              </Button>
            )}
            <Button size="large" className="h-11 px-6 rounded-xl border-gray-200 text-gray-700 w-full sm:w-auto hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/search')}>
              浏览书库
            </Button>
            <Button size="large" className="h-11 px-6 rounded-xl border-gray-200 text-gray-700 w-full sm:w-auto hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/tts')}>
              文本转语音
            </Button>
            {isAuthenticated ? (
              <Button size="large" className="h-11 px-6 rounded-xl border-gray-200 text-gray-700 w-full sm:w-auto hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate((user?.role === 'admin' || user?.role === 'super_admin') ? '/admin' : '/profile')}>
                {(user?.role === 'admin' || user?.role === 'super_admin') ? '后台管理' : '个人中心'}
              </Button>
            ) : (
              <Button size="large" className="h-11 px-6 rounded-xl border-gray-200 text-gray-700 w-full sm:w-auto hover:border-primary-400 hover:text-primary-600 bg-white shadow-sm" onClick={() => navigate('/login')}>
                登录/注册
              </Button>
            )}
          </div>
        </div>
      </div>
      {resumePath ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-amber-800">
            发现你上次的阅读进度
            {resumeAt > 0 ? `（${new Date(resumeAt).toLocaleString()}）` : ''}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="small" type="primary" onClick={handleContinueReading}>
              继续阅读
            </Button>
            <Button size="small" onClick={handleDismissContinueReading}>
              暂不继续
            </Button>
          </div>
        </div>
      ) : null}
      
      {marqueeContent && (
        <div
          ref={marqueeContainerRef}
          className="bg-gradient-to-r from-orange-50 via-amber-50 to-orange-50 text-orange-600 font-normal px-4 py-3 rounded-lg border border-orange-100 overflow-hidden relative marquee-container flex items-center text-sm shadow-sm"
        >
          <span
            ref={marqueeMeasureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              left: -99999,
              top: -99999,
            }}
          >
            {marqueeContent}
          </span>
          <div
            className="animate-marquee"
            style={{
              ['--marquee-shift' as never]: `${marqueeShiftPx || 0}px`,
              ['--marquee-duration' as never]: `${marqueeDurationSec || 30}s`,
              ['--marquee-gap' as never]: `${marqueeGapPx || 64}px`,
            }}
          >
            {Array.from({ length: marqueeRepeat }, (_, i) => (
              <div key={i}>{marqueeContent}</div>
            ))}
          </div>
        </div>
      )}

      <Row gutter={[16,16]}>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><BookOutlined className="text-primary-500" />册数</span>}
              value={stats.books}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><ImportOutlined className="text-primary-500" />新增</span>}
              value={stats.today}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
        <Col xs={8} sm={8} md={8}>
          <Card size="small" className="theme-card border-none" styles={{ body: { padding: '8px 12px' } }}>
            <Statistic
              title={<span className="inline-flex items-center gap-2 text-gray-500"><SearchOutlined className="text-primary-500" />热榜</span>}
              value={hotCount}
              valueStyle={{ fontSize: 18, lineHeight: 1.1, fontWeight: 600, color: 'var(--color-text-main)' }}
            />
          </Card>
        </Col>
      </Row>
      <Row gutter={[24,24]}>
        <Col xs={24} md={24}>
          <Card
            title={<span className="text-lg font-bold text-gray-800">推荐榜</span>}
            className="theme-card border-none mb-6"
            styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
          >
            {publicRecs.length > 0 ? (
              <Row gutter={[16, 16]}>
                {pagedPublicRecs.map((item, idx) => (
                  <Col xs={12} sm={8} md={6} lg={6} xl={4} key={`${item.source}-${item.id}`}>
                    <Card
                      hoverable
                      styles={{ body: { padding: 12 } }}
                      className="h-full border-none shadow-sm hover:shadow-theme-md transition-all duration-300 rounded-lg overflow-hidden group"
                      onClick={() => navigate(`/reader/${item.id}`)}
                      cover={
                        <div className="relative w-full overflow-hidden bg-gray-100" style={{ aspectRatio: '3 / 4' }}>
                          <img
                            src={getBookCoverUrl(item.id)}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              const img = e.currentTarget
                              if (img.dataset.fallbackApplied === '1') return
                              img.dataset.fallbackApplied = '1'
                              img.src = '/icons/My_logo.png'
                              img.style.objectFit = 'contain'
                              img.style.objectPosition = 'center'
                              img.style.transform = 'scale(0.5)'
                              img.style.opacity = '0.35'
                              img.style.filter = 'grayscale(0.15)'
                            }}
                          />
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center justify-center text-xs font-semibold text-white bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded shadow-sm">
                              #{(publicRecsPage - 1) * publicRecsPageSize + idx + 1}
                            </span>
                          </div>
                          {item.source === 'strong' ? (
                            <div className="absolute top-2 right-2">
                              <Tag color="#ef4444" style={{ marginInlineEnd: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', border: 'none' }}>强推荐</Tag>
                            </div>
                          ) : null}
                        </div>
                      }
                    >
                      <div className="text-gray-800" style={{ fontWeight: 600, lineHeight: 1.4, marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '2.8em' }}>
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.author}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">ID: {item.id}</div>
                      <div className="text-xs text-primary-600 mt-1 bg-primary-50 inline-block px-1.5 py-0.5 rounded">{item.format}</div>
                      {item.source === 'rating' ? (
                        <div className="mt-2 flex items-center gap-1">
                          <Rate disabled allowHalf value={Number(item.avg_rating || 0)} style={{ fontSize: 12, color: '#f59e0b' }} />
                          <div className="text-xs text-gray-400 transform scale-90 origin-left">{Number(item.rating_count || 0)}人</div>
                        </div>
                      ) : null}
                    </Card>
                  </Col>
                ))}
                <Col span={24}>
                  <div className="flex justify-end mt-4">
                    <Pagination
                      current={publicRecsPage}
                      pageSize={publicRecsPageSize}
                      total={publicRecs.length}
                      showSizeChanger
                      pageSizeOptions={[12, 24, 48, 96]}
                      showQuickJumper
                      onChange={(p, ps) => {
                        setPublicRecsPage(p)
                        if (typeof ps === 'number' && ps !== publicRecsPageSize) {
                          setPublicRecsPageSize(ps)
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
            ) : (
              <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">暂无推荐</div>
            )}
          </Card>
        </Col>
      </Row>

      {canViewRecentImports && (
      <Card
        title={<span className="text-lg font-bold text-gray-800">最近导入</span>}
        className="theme-card border-none mb-6"
        styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Select
            size="small"
            value={sortField}
            onChange={(v) => setSortField(v as 'title' | 'author' | 'format' | 'size' | 'uploadTime')}
            options={[
              { value: 'title', label: '书名' },
              { value: 'author', label: '作者' },
              { value: 'format', label: '格式' },
              { value: 'size', label: '大小' },
              { value: 'uploadTime', label: '上传时间' },
            ]}
            style={{ width: 120 }}
          />
          <Select
            size="small"
            value={sortOrder}
            onChange={(v) => setSortOrder(v as 'ascend' | 'descend')}
            options={[{ value: 'ascend', label: '升序' }, { value: 'descend', label: '降序' }]}
            style={{ width: 100 }}
          />
        </div>
        <div style={{ maxHeight: 10 * 64, overflowY: 'auto' }} className="space-y-2 pr-2 custom-scrollbar">
          {sortedRecentBooks.length > 0 ? (
            sortedRecentBooks.map((book) => (
              <div key={book.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-primary-50 hover:shadow-sm transition-all cursor-pointer border border-transparent hover:border-primary-100" onClick={() => navigate(`/reader/${book.id}`)}>
                <div>
                  <h4 className="font-medium text-gray-800">{book.title} <span className="ml-2 text-xs text-gray-500">ID: {book.id}</span> <span className="ml-2 text-xs text-gray-500">{book.author || '未知作者'}</span></h4>
                  <p className="text-xs md:text-sm text-gray-500 mt-1 flex items-center gap-2">
                    <span className="bg-white px-1.5 py-0.5 rounded text-xs border border-gray-200">{book.format}</span>
                    <span>{book.size}</span>
                  </p>
                </div>
                <span className="text-xs md:text-sm text-gray-400">{book.uploadTime}</span>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-12 bg-gray-50 rounded-lg">暂无数据</div>
          )}
        </div>
      </Card>
      )}

      <footer className="pt-2 pb-6 text-center text-xs md:text-sm text-gray-400 leading-relaxed">
        <div className="px-3">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span>© 2026-现在 Bandume.cn 伴读么 版权所有</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noreferrer"
              className="text-gray-400 hover:text-gray-500 underline underline-offset-2 decoration-gray-300"
            >
              苏ICP备2025159326号-3
            </a>
          </div>
          <div className="mt-1">
            建议您使用Edge、Chrome 80+、FireFox 86+、360极速模式等主流浏览器浏览本站
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
